import type { ApiFailure, ApiResponse, ApiErrorCode, ApiFieldError } from '@insurensa/shared';

/**
 * API client for the admin console.
 *
 * Two things matter here:
 *
 *  1. The access token is held in a module-scoped variable, never in
 *     localStorage. A token in localStorage is readable by any script that gets
 *     onto the page; this one dies with the tab. Session continuity comes from
 *     the HttpOnly refresh cookie instead, which script cannot read at all.
 *
 *  2. A 401 triggers exactly one refresh attempt, and concurrent requests share
 *     it, so a page with six panels does not fire six refreshes and invalidate
 *     its own rotating token.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const API = `${BASE_URL}/api/v1`;

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

/** Called by the auth layer on sign-in and refresh. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields: ApiFieldError[];
  readonly requestId: string | undefined;

  constructor(status: number, failure: ApiFailure['error']) {
    super(failure.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = failure.code;
    this.fields = failure.fields ?? [];
    this.requestId = failure.requestId;
  }

  /** Message for a specific form field, for inline validation display. */
  fieldError(path: string): string | undefined {
    return this.fields.find((field) => field.path === path)?.message;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return false;
      const body = (await response.json()) as ApiResponse<{ accessToken: string }>;
      if (!body.ok) return false;
      accessToken = body.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Clear on the next tick so callers awaiting this promise all observe the
      // same result before a new attempt can start.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** FormData for uploads; Content-Type is left to the browser. */
  formData?: FormData;
  signal?: AbortSignal;
  /** Internal: prevents a refresh loop. */
  retried?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, signal, retried = false } = options;

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${API}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(formData ? { body: formData } : {}),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, {
      code: 'INTERNAL_ERROR',
      message: 'Unerwartete Antwort vom Server',
    });
  }

  if (payload.ok) return payload.data;

  // One refresh, then one retry. Never on the refresh endpoint itself.
  if (
    response.status === 401 &&
    !retried &&
    !path.startsWith('/auth/refresh') &&
    !path.startsWith('/auth/admin/login')
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request<T>(path, { ...options, retried: true });
    }
    accessToken = null;
    window.dispatchEvent(new CustomEvent('insurensa:session-expired'));
  }

  throw new ApiError(response.status, payload.error);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
};

/** Builds a query string, dropping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
